import { useEffect, useRef } from 'react';

// Read inactivity timeout (minutes) from env; default to 60 minutes if unset/invalid.
const DEFAULT_INACTIVITY_MINUTES = (() => {
    const raw = process.env.REACT_APP_INACTIVITY_TIMEOUT_MINUTES || '60';
    const n = parseInt(String(raw || '').trim(), 10);
    return Number.isInteger(n) && n > 0 ? n : 60;
})();
const DEFAULT_INACTIVITY_MS = DEFAULT_INACTIVITY_MINUTES * 60 * 1000;

/**
 * useNavigationInactivity
 * - Implements a sliding session window: resets the inactivity timer on each route navigation.
 * - Session is maintained as long as the user remains active (navigating).
 * - When the timer expires (60 minutes of inactivity) it calls the provided logoutFn.
 * - Broadcasts activity and logout events across tabs using BroadcastChannel with a localStorage fallback.
 *
 * @param {Function} logoutFn - function to call when inactivity timeout fires
 * @param {number} inactivityMs - milliseconds of inactivity before triggering logout (default from REACT_APP_INACTIVITY_TIMEOUT_MINUTES or 60 minutes)
 */
export default function useNavigationInactivity(logoutFn, inactivityMs = DEFAULT_INACTIVITY_MS) {
    const inactivityTimerRef = useRef(null);

    const resetInactivityTimer = () => {
        if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
        }
        inactivityTimerRef.current = setTimeout(() => {
            try {
                logoutFn();
                if (typeof BroadcastChannel !== 'undefined') {
                    try {
                        const bc = new BroadcastChannel('auth');
                        bc.postMessage({ type: 'logged_out' });
                        bc.close();
                    } catch (err) {
                        // ignore
                    }
                } else {
                    try {
                        localStorage.setItem('prism_logged_out', Date.now());
                    } catch (e) {}
                }
            } catch (e) {
                // swallow - caller will handle failures
            }
        }, inactivityMs);
    };

    useEffect(() => {
        // start timer on mount
        resetInactivityTimer();

        // listen for cross-tab messages
        let bc;
        if (typeof BroadcastChannel !== 'undefined') {
            try {
                bc = new BroadcastChannel('auth');
                bc.onmessage = (evt) => {
                    if (!evt.data) return;
                    if (evt.data.type === 'activity') resetInactivityTimer();
                    if (evt.data.type === 'logged_out') {
                        try {
                            logoutFn();
                        } catch (e) {}
                    }
                };
            } catch (err) {
                bc = null;
            }
        } else {
            const storageHandler = (e) => {
                if (!e.key) return;
                if (e.key === 'prism_activity') resetInactivityTimer();
                if (e.key === 'prism_logged_out') {
                    try {
                        logoutFn();
                    } catch (e) {}
                }
            };
            window.addEventListener('storage', storageHandler);
            return () => {
                if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
                window.removeEventListener('storage', storageHandler);
            };
        }

        return () => {
            if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
            if (bc) bc.close();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        resetInactivityTimer,
    };
}


